import { Benchmark, FieldModel } from '@cibo/core'
import { Markdown } from '@cibo/ui'
import { Box, Divider, Stack, Typography } from '@mui/material'
import { partition } from 'ramda'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { BenchmarkStatusReasons, ProgramIcon, useOpenPrograms } from '../..'

interface AllProgramReasonsProps {
  field: FieldModel
  benchmarks?: Benchmark[]
}

export const AllProgramReasons = ({ benchmarks, field }: AllProgramReasonsProps) => {
  const { t } = useTranslation('@cibo/programs/components/AllProgramReasons')
  const { openPrograms, isFetching } = useOpenPrograms()

  const programReasons = useMemo(
    () =>
      field.programStatuses
        ?.filter(p => openPrograms.find(program => program.programId === p.programId))
        .filter(p => !p.workflow)
        .map(p => ({
          reasons: field.reasons(p.programId)?.filter(({ hasResult }) => hasResult) || [],
          program: openPrograms.find(({ programId }) => programId === p.programId),
        }))
        .filter(({ reasons }) => !!reasons.length),
    [isFetching, field]
  )

  if (!programReasons) return null

  const [geometryReasons, otherReasons] = partition(
    a => a.reasons.some(r => r.traitId === 'geometry'),
    programReasons
  )

  const geometryBasedReasons = {
    notInArea: geometryReasons.filter(({ reasons }) =>
      reasons.some(reason => reason.reason === 'notInArea')
    ),
    wouldExceedCaps: geometryReasons.filter(({ reasons }) =>
      reasons.some(reason => reason.reason === 'wouldExceedCaps')
    ),
    alreadyEnrolledInProgramSharingLedger: geometryReasons.filter(({ reasons }) =>
      reasons.some(reason => reason.reason === 'alreadyEnrolledInProgramSharingLedger')
    ),
    ledgerConflict: geometryReasons.filter(({ reasons }) =>
      reasons.some(reason => reason.reason === 'ledgerConflict')
    ),
    fieldSizeNotInValidRange: geometryReasons.filter(({ reasons }) =>
      reasons.some(reason => reason.reason === 'fieldSizeNotInValidRange')
    ),
  }

  return (
    <Stack spacing={5}>
      {otherReasons.map(({ program, reasons }) => (
        <Stack key={program?.programId} spacing={1}>
          {!!program && (
            <Stack>
              <Stack direction="row" spacing={2}>
                <ProgramIcon program={program.program} size={24} />
                <Typography variant="h6">{program.program.content.fields.title}</Typography>
              </Stack>
              <Box paddingLeft={5}>
                <Markdown color="text.secondary">
                  {program?.program.content.fields.shortDescription}
                </Markdown>
              </Box>
            </Stack>
          )}
          <Box paddingLeft={8}>
            <BenchmarkStatusReasons fieldId={field.id} reasons={reasons} />
          </Box>
        </Stack>
      ))}
      {Object.keys(geometryBasedReasons).map(
        key =>
          geometryBasedReasons[key as keyof typeof geometryBasedReasons].length > 0 && (
            <>
              <Divider />
              <Typography variant="body2" color="text.secondary">
                {t(key, {
                  count: geometryBasedReasons[key as keyof typeof geometryBasedReasons].length,
                })}
              </Typography>
              <Stack spacing={2}>
                {geometryBasedReasons[key as keyof typeof geometryBasedReasons].map(({ program }) =>
                  !!program ? (
                    <Stack>
                      <Stack key={program.programId} direction="row" spacing={2}>
                        <ProgramIcon program={program.program} size={24} />
                        <Typography variant="h6">{program.program.content.fields.title}</Typography>
                      </Stack>
                      <Box paddingLeft={8}>
                        <Markdown color="text.secondary">
                          {program?.program.content.fields.shortDescription}
                        </Markdown>
                      </Box>
                    </Stack>
                  ) : null
                )}
              </Stack>
            </>
          )
      )}
    </Stack>
  )
}
